import { User } from 'lucide-react';
import { useExternalValueSync } from '../../hooks/useExternalValueSync';
import { type PatientMetrics } from '../../utils/peptideCalculations';

interface PatientProfileProps {
  sex: 'male' | 'female';
  age: number;
  weight: number;
  weightUnit: 'lbs' | 'kg';
  height: number;
  heightUnit: 'in' | 'cm';
  metrics: PatientMetrics;
  onChange: (field: string, value: string | number) => void;
}

export function PatientProfileSection({
  sex, age, weight, weightUnit, height, heightUnit, metrics, onChange,
}: PatientProfileProps) {
  // Uncontrolled fields that still follow an externally changed value. These
  // previously carried key={`age-${age}`} and friends, which remounted the input
  // on every keystroke and threw the cursor out of the field.
  const ageRef = useExternalValueSync<HTMLInputElement>(age);
  const weightRef = useExternalValueSync<HTMLInputElement>(weight);
  const heightRef = useExternalValueSync<HTMLInputElement>(height);

  return (
    <div className="bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-xl shadow-sm p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-bold flex items-center gap-2">
          <User className="w-4 h-4" />
          Patient Profile
        </h2>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Sex</label>
          <select
            value={sex}
            onChange={e => onChange('sex', e.target.value)}
            className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
          >
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>

        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Age (yrs)</label>
          <input
            type="number"
            ref={ageRef}
            defaultValue={age}
            min={18}
            max={100}
            onChange={e => { const v = parseInt(e.target.value); if (!isNaN(v)) onChange('age', v); }}
            onBlur={e => { const v = parseInt(e.target.value); if (isNaN(v)) { onChange('age', 0); e.target.value = '0'; } }}
            className="w-full bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
          />
        </div>

        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Weight</label>
          <div className="flex gap-1">
            <input
              type="number"
              ref={weightRef}
              defaultValue={weight}
              min={50}
              max={500}
              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) onChange('weight', v); }}
              onBlur={e => { const v = parseFloat(e.target.value); if (isNaN(v)) { onChange('weight', 0); e.target.value = '0'; } }}
              className="flex-1 min-w-0 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
            />
            <select
              value={weightUnit}
              onChange={e => onChange('weightUnit', e.target.value)}
              className="w-16 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-1 py-2 text-sm font-mono outline-none focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
            >
              <option value="lbs">lbs</option>
              <option value="kg">kg</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-1">Height</label>
          <div className="flex gap-1">
            <input
              type="number"
              ref={heightRef}
              defaultValue={height}
              min={48}
              max={250}
              onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v)) onChange('height', v); }}
              onBlur={e => { const v = parseFloat(e.target.value); if (isNaN(v)) { onChange('height', 0); e.target.value = '0'; } }}
              className="flex-1 min-w-0 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-2.5 py-2 text-sm font-mono outline-none focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
            />
            <select
              value={heightUnit}
              onChange={e => onChange('heightUnit', e.target.value)}
              className="w-16 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-lg px-1 py-2 text-sm font-mono outline-none focus:border-blue-500 dark:focus:border-blue-400 transition-colors"
            >
              <option value="in">in</option>
              <option value="cm">cm</option>
            </select>
          </div>
        </div>
      </div>

      {/* Stat badges */}
      <div className="flex flex-wrap gap-2 mt-3">
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
          BMI {metrics.bmi.toFixed(1)}
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-violet-500/10 text-violet-600 dark:text-violet-400">
          BSA {metrics.bsa.toFixed(2)} m²
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-cyan-500/10 text-cyan-600 dark:text-cyan-400">
          IBW {metrics.ibw.toFixed(0)} kg
        </span>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400">
          {metrics.weightKg.toFixed(1)} kg
        </span>
      </div>
    </div>
  );
}
